import React, { useEffect, useState } from "react";
import { api, formatUAH, formatNum } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import PeriodFilter from "@/components/PeriodFilter";

const COLORS = ["#0F2027", "#0D9488", "#10B981", "#F59E0B", "#94A3B8"];

function MiniStat({ label, value, tone = "navy" }) {
  const cls = { navy: "text-[#0F2027]", teal: "text-[#0D9488]", green: "text-emerald-700", red: "text-red-600" }[tone];
  return (
    <Card className="gv-card"><CardContent className="p-5">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold tabular-nums ${cls}`}>{value}</div>
    </CardContent></Card>
  );
}

function ProfitTable({ rows, testid }) {
  return (
    <Card className="gv-card" data-testid={testid}><CardContent className="p-0 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
          <tr>
            <th className="text-left p-4">ПІБ</th>
            <th className="text-right p-4">Операції</th>
            <th className="text-right p-4">Виручка (частка)</th>
            <th className="text-right p-4">Собівартість</th>
            <th className="text-right p-4">Прибуток</th>
            <th className="text-right p-4">Маржа</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.employee_id} className="border-t border-slate-100">
              <td className="p-4 font-medium">{r.full_name}</td>
              <td className="p-4 text-right tabular-nums">{formatNum(r.operations)}</td>
              <td className="p-4 text-right tabular-nums">{formatUAH(r.revenue)}</td>
              <td className="p-4 text-right tabular-nums text-slate-600">{formatUAH(r.cost)}</td>
              <td className="p-4 text-right tabular-nums font-semibold text-emerald-700">{formatUAH(r.gross_profit)}</td>
              <td className="p-4 text-right tabular-nums">{r.margin}%</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">Немає даних</td></tr>}
        </tbody>
      </table>
    </CardContent></Card>
  );
}

export default function Profitability() {
  const [period, setPeriod] = useState({});
  const [data, setData] = useState({ overview: null, doctors: [], surgeons: [], operations: [] });

  useEffect(() => {
    if (period.from === undefined && period.to === undefined) return;
    const params = new URLSearchParams();
    if (period.from) params.set("from_date", period.from);
    if (period.to) params.set("to_date", period.to);
    Promise.all([
      api.get(`/profitability/overview?${params}`),
      api.get(`/profitability/by-doctor?${params}`),
      api.get(`/profitability/by-surgeon?${params}`),
      api.get(`/profitability/by-operation?${params}`),
    ]).then(([o, d, s, ops]) => setData({ overview: o.data, doctors: d.data, surgeons: s.data, operations: ops.data }));
  }, [period]);

  const o = data.overview;

  const breakdown = o ? [
    { name: "ІОЛ", value: o.cost_iol },
    { name: "Витратні матеріали", value: o.cost_materials },
    { name: "Лазерні процедури", value: o.cost_laser },
    { name: "Інші витрати", value: o.cost_other },
  ].filter((x) => x.value > 0) : [];

  return (
    <div data-testid="profitability-page" className="space-y-6">
      <div className="flex justify-end"><PeriodFilter value={period} onChange={setPeriod} defaultPreset="ytd" /></div>
      {!o && <div className="text-slate-500">Завантаження...</div>}
      {o && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <MiniStat label="Виручка" value={formatUAH(o.revenue)} />
            <MiniStat label="Собівартість" value={formatUAH(o.cost_total)} tone="red" />
            <MiniStat label="Валовий прибуток" value={formatUAH(o.gross_profit)} tone="teal" />
            <MiniStat label="Чистий прибуток" value={formatUAH(o.net_profit)} tone="green" />
            <MiniStat label="Маржа" value={`${o.margin_percent}%`} tone="teal" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="gv-card lg:col-span-2"><CardContent className="p-6">
              <h3 className="heading font-semibold text-[#0F2027] mb-3">Структура собівартості</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="p-3 border border-slate-200 rounded-md"><div className="text-xs text-slate-500">ІОЛ</div><div className="font-bold tabular-nums">{formatUAH(o.cost_iol)}</div></div>
                <div className="p-3 border border-slate-200 rounded-md"><div className="text-xs text-slate-500">Витратні матеріали</div><div className="font-bold tabular-nums">{formatUAH(o.cost_materials)}</div></div>
                <div className="p-3 border border-slate-200 rounded-md"><div className="text-xs text-slate-500">Лазерні процедури</div><div className="font-bold tabular-nums">{formatUAH(o.cost_laser)}</div></div>
                <div className="p-3 border border-slate-200 rounded-md"><div className="text-xs text-slate-500">Інші витрати</div><div className="font-bold tabular-nums">{formatUAH(o.cost_other)}</div></div>
              </div>
            </CardContent></Card>
            <Card className="gv-card"><CardContent className="p-6">
              <h3 className="heading font-semibold text-[#0F2027] mb-3">Розподіл витрат</h3>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={breakdown} dataKey="value" nameKey="name" outerRadius={70} innerRadius={40}>
                      {breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => formatUAH(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent></Card>
          </div>

          <Tabs defaultValue="doctors">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="doctors" data-testid="profit-tab-doctors">По лікарях</TabsTrigger>
              <TabsTrigger value="surgeons" data-testid="profit-tab-surgeons">По хірургах</TabsTrigger>
              <TabsTrigger value="ops" data-testid="profit-tab-ops">По операціях</TabsTrigger>
            </TabsList>
            <TabsContent value="doctors" className="mt-3"><ProfitTable rows={data.doctors} testid="profit-table-doctors" /></TabsContent>
            <TabsContent value="surgeons" className="mt-3"><ProfitTable rows={data.surgeons} testid="profit-table-surgeons" /></TabsContent>
            <TabsContent value="ops" className="mt-3">
              <Card className="gv-card"><CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
                    <tr>
                      <th className="text-left p-4">Дата</th>
                      <th className="text-left p-4">Пацієнт</th>
                      <th className="text-left p-4">Операція</th>
                      <th className="text-right p-4">Виручка</th>
                      <th className="text-right p-4">Собівартість</th>
                      <th className="text-right p-4">Прибуток</th>
                      <th className="text-right p-4">Маржа</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.operations.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="p-4 text-slate-600">{r.date}</td>
                        <td className="p-4 font-medium">{r.patient}</td>
                        <td className="p-4 text-slate-700">{r.operation_type}</td>
                        <td className="p-4 text-right tabular-nums">{formatUAH(r.revenue)}</td>
                        <td className="p-4 text-right tabular-nums text-slate-600">{formatUAH(r.cost)}</td>
                        <td className="p-4 text-right tabular-nums font-semibold text-emerald-700">{formatUAH(r.gross_profit)}</td>
                        <td className="p-4 text-right tabular-nums">{r.margin}%</td>
                      </tr>
                    ))}
                    {data.operations.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">Немає даних</td></tr>}
                  </tbody>
                </table>
              </CardContent></Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
